import React, { useEffect, useMemo, useState } from "react";
import {
  Button,
  Card,
  DatePicker,
  Divider,
  Empty,
  Spin,
  Table,
  Toast,
  TreeSelect,
  Typography,
  Progress,
  Tag,
} from "@douyinfe/semi-ui";
import {
  IconRefresh,
  IconCalendar,
  IconPhone,
  IconTick,
  IconClock,
  IconUser,
  IconCaretdown,
  IconCaretup,
} from "@douyinfe/semi-icons";
import dayjs from "dayjs";
import { ApiClient, CallStatRow } from "../../api/ApiClient";

const { Text } = Typography;
const apiClient = ApiClient.getDefault();

// ===== 常量 =====
const COLORS = {
  success: "#34c724",
  warning: "#ff7d00",
  danger: "#f5222d",
  bgCard: "var(--semi-color-bg-1)",
  bgPage: "var(--semi-color-bg-0)",
  border: "var(--semi-color-border)",
  textPrimary: "var(--semi-color-text-0)",
  textSecondary: "var(--semi-color-text-1)",
  textTertiary: "var(--semi-color-text-2)",
};

// 树节点指标
interface Metrics {
  contactCustomers: number;
  callCount: number;
  connectedCount: number;
  unconnectedCount: number;
  effectiveCount: number;
  totalDuration: number;
}

// 树形表格行
interface TreeRow {
  key: string;
  name: string;
  sub: string; // 团队 + 角色
  isManager: boolean;
  metrics: Metrics & { avgDuration: number; connectRate: number };
  children?: TreeRow[];
}

const EMPTY_METRICS = {
  contactCustomers: 0, callCount: 0, connectedCount: 0,
  unconnectedCount: 0, effectiveCount: 0, totalDuration: 0,
};

// 定位以指定节点为根的子树
function findSubtree(nodes: any[], targetId: number): any[] | null {
  for (const n of nodes) {
    if (Number(n.value) === targetId) return [n];
    if (n.children?.length) {
      const found = findSubtree(n.children, targetId);
      if (found) return found;
    }
  }
  return null;
}

// 收集子树全部成员 id（含根节点）
function collectSubtreeIds(nodes: any[]): number[] {
  const ids: number[] = [];
  const walk = (list: any[]) => list.forEach((n) => {
    ids.push(Number(n.value));
    if (n.children?.length) walk(n.children);
  });
  walk(nodes);
  return ids;
}

// ===== 工具函数 =====
function formatDuration(sec: number): string {
  if (sec == null || isNaN(sec) || sec <= 0) return "-";
  if (sec < 60) return `${Math.round(sec)}秒`;
  const m = Math.floor(sec / 60);
  const s = Math.round(sec % 60);
  return `${m}分${s}秒`;
}

function formatHours(sec: number): string {
  if (sec == null || isNaN(sec) || sec <= 0) return "-";
  if (sec < 60) return `${Math.round(sec)}秒`;
  const h = Math.floor(sec / 3600);
  const m = Math.round((sec % 3600) / 60);
  return h > 0 ? `${h}h ${m}m` : `${m}m`;
}

function percentText(rate: number): string {
  if (rate == null || isNaN(rate)) return "-";
  return `${rate.toFixed(1)}%`;
}

// ===== KPI 卡片 =====
function KpiCard({ title, value, unit, icon, color, subtitle }: {
  title: string; value: string | number; unit?: string;
  icon: React.ReactNode; color: string; subtitle?: string;
}) {
  return (
    <div style={{
      background: COLORS.bgCard, borderRadius: 12, padding: "16px 20px",
      border: `1px solid ${COLORS.border}`, position: "relative", overflow: "hidden",
    }}>
      <div style={{ position: "absolute", top: -20, right: -20, width: 80, height: 80, borderRadius: "50%", background: `${color}10`, display: "flex", alignItems: "center", justifyContent: "center" }}>
        <div style={{ color: `${color}30`, fontSize: 40 }}>{icon}</div>
      </div>
      <div style={{ position: "relative", zIndex: 1 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
          <div style={{
            width: 32, height: 32, borderRadius: 8, background: `${color}15`,
            display: "flex", alignItems: "center", justifyContent: "center", color,
          }}>
            {icon}
          </div>
          <Text style={{ color: COLORS.textSecondary, fontSize: 13 }}>{title}</Text>
        </div>
        <div style={{ display: "flex", alignItems: "baseline", gap: 4 }}>
          <span style={{ fontSize: 28, fontWeight: 700, color: COLORS.textPrimary, lineHeight: 1 }}>{value}</span>
          {unit && <span style={{ fontSize: 14, color: COLORS.textSecondary }}>{unit}</span>}
        </div>
        {subtitle && <div style={{ marginTop: 8, fontSize: 12, color: COLORS.textTertiary }}>{subtitle}</div>}
      </div>
    </div>
  );
}

// ===== 主组件 =====
export default function CallStat() {
  const today = dayjs();
  const [dateRange, setDateRange] = useState<[Date, Date]>([today.startOf("month").toDate(), today.toDate()]);
  const [saleId, setSaleId] = useState<number | null>(null);

  const [appliedReq, setAppliedReq] = useState({
    callAtStart: today.startOf("month").format("YYYY-MM-DD"),
    callAtEnd: today.format("YYYY-MM-DD"),
    saleId: null as number | null,
  });

  const [managerTree, setManagerTree] = useState<any[]>([]);
  const [statRows, setStatRows] = useState<CallStatRow[]>([]);
  const [summary, setSummary] = useState<CallStatRow | null>(null);
  const [loading, setLoading] = useState(false);

  // 排序状态（数字列）
  type SortKey = keyof TreeRow["metrics"]; // 含 avgDuration / connectRate
  const [sort, setSort] = useState<{ key: SortKey | null; order: "asc" | "desc" }>({ key: null, order: "desc" });

  useEffect(() => {
    window.document.title = "通话统计";
    // 管理员从 root 看全树，非管理员看管辖（复用客户触达分析的口径）
    const role = localStorage.getItem("role");
    const username = localStorage.getItem("username");
    const currUsername = ["op_manager", "monitor", "foreground"].includes(role || "") ? "root" : username;
    apiClient.getTeamMemberTreeAsOptsOfAny(currUsername).then(setManagerTree).catch(() => {});
  }, []);

  // 查询范围 = 所选销售 + 全部下级（与"含下级"的树形口径一致），未选则查全部
  // 注意：树节点 value 是字符串（user.id + ""），TreeSelect 回传也是字符串，比较前必须转数字
  const querySaleIds = useMemo(() => {
    if (appliedReq.saleId == null || managerTree.length === 0) return null;
    const subtree = findSubtree(managerTree, Number(appliedReq.saleId));
    return subtree ? collectSubtreeIds(subtree) : null;
  }, [managerTree, appliedReq.saleId]);

  useEffect(() => {
    setLoading(true);
    apiClient
      .getCallStat(appliedReq.callAtStart, appliedReq.callAtEnd, querySaleIds)
      .then((resp) => {
        setStatRows(resp.data?.rows ?? []);
        setSummary(resp.data?.summary ?? null);
      })
      .catch(() => Toast.error("数据加载失败"))
      .finally(() => setLoading(false));
  }, [appliedReq, querySaleIds]);

  const handleFilter = () => {
    const [start, end] = dateRange;
    setAppliedReq({
      callAtStart: dayjs(start).format("YYYY-MM-DD"),
      callAtEnd: dayjs(end).format("YYYY-MM-DD"),
      saleId,
    });
  };

  const handlePreset = (kind: "today" | "7d" | "30d" | "month") => {
    const presets: Record<string, [dayjs.Dayjs, dayjs.Dayjs]> = {
      today: [today, today],
      "7d": [today.subtract(6, "day"), today],
      "30d": [today.subtract(29, "day"), today],
      month: [today.startOf("month"), today],
    };
    const [start, end] = presets[kind];
    setDateRange([start.toDate(), end.toDate()]);
  };

  // ===== 树构建：树选项 + 统计行 → 汇总树 =====
  const statsMap = useMemo(() => {
    const m = new Map<number, CallStatRow>();
    statRows.forEach((r) => m.set(r.saleId, r));
    return m;
  }, [statRows]);

  // 应用了查询的销售后，把树裁剪为以该销售为根的子树（与查询口径一致，避免选了未查询时树和数据对不上）
  const filteredTree = useMemo(() => {
    if (appliedReq.saleId == null) return managerTree;
    return findSubtree(managerTree, Number(appliedReq.saleId)) ?? managerTree;
  }, [managerTree, appliedReq.saleId]);

  const buildRow = (node: any): TreeRow => {
    const row = statsMap.get(Number(node.value));
    const own: Metrics = row ? {
      contactCustomers: row.contactCustomers,
      callCount: row.callCount,
      connectedCount: row.connectedCount,
      unconnectedCount: row.unconnectedCount,
      effectiveCount: row.effectiveCount,
      totalDuration: row.totalDuration,
    } : { ...EMPTY_METRICS };

    const childRows: TreeRow[] = (node.children ?? []).map(buildRow);

    // 汇总 = 本人 + 子孙求和（联系客户数为成员求和，可能大于去重客户数）
    const agg: Metrics = { ...own };
    childRows.forEach((c) => {
      agg.contactCustomers += c.metrics.contactCustomers;
      agg.callCount += c.metrics.callCount;
      agg.connectedCount += c.metrics.connectedCount;
      agg.unconnectedCount += c.metrics.unconnectedCount;
      agg.effectiveCount += c.metrics.effectiveCount;
      agg.totalDuration += c.metrics.totalDuration;
    });

    return {
      key: node.value,
      name: node.label,
      sub: node.labelSubText || "",
      isManager: childRows.length > 0,
      metrics: {
        ...agg,
        avgDuration: agg.callCount > 0 ? Math.round((agg.totalDuration / agg.callCount) * 10) / 10 : 0,
        connectRate: agg.callCount > 0 ? Math.round((agg.connectedCount * 1000) / agg.callCount) / 10 : 0,
      },
      children: childRows.length > 0 ? childRows : undefined,
    };
  };

  // 排序：递归对每一层排序
  const sortTree = (nodes: TreeRow[]): TreeRow[] => {
    const sorted = [...nodes].sort((a, b) => {
      if (!sort.key) return 0;
      const av = (a.metrics as any)[sort.key] ?? 0;
      const bv = (b.metrics as any)[sort.key] ?? 0;
      return sort.order === "asc" ? av - bv : bv - av;
    });
    return sorted.map((n) => (n.children ? { ...n, children: sortTree(n.children) } : n));
  };

  const treeData = useMemo(() => {
    const rows = filteredTree.map(buildRow);
    return sort.key ? sortTree(rows) : rows;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filteredTree, statsMap, sort]);

  // 展开状态：数据变化后回到全展开，可手动折叠
  const allParentKeys = useMemo(() => {
    const keys: string[] = [];
    const walk = (nodes: TreeRow[]) => nodes.forEach((n) => {
      if (n.children?.length) {
        keys.push(n.key);
        walk(n.children);
      }
    });
    walk(treeData);
    return keys;
  }, [treeData]);

  const [expandedKeys, setExpandedKeys] = useState<string[]>([]);
  useEffect(() => {
    setExpandedKeys(allParentKeys);
  }, [allParentKeys]);

  // ===== 排序表头 =====
  const sortHeader = (title: string, key: SortKey) => (
    <span
      style={{ cursor: "pointer", userSelect: "none", display: "inline-flex", alignItems: "center", gap: 2 }}
      onClick={() =>
        setSort((s) =>
          s.key === key
            ? s.order === "desc"
              ? { key, order: "asc" }
              : { key: null, order: "desc" }
            : { key, order: "desc" }
        )
      }
    >
      {title}
      {sort.key === key ? (
        sort.order === "desc" ? <IconCaretdown size="small" /> : <IconCaretup size="small" />
      ) : (
        <IconCaretdown size="small" style={{ opacity: 0.25 }} />
      )}
    </span>
  );

  // 数字列渲染：父行加粗，突出层级
  const numRender = (getValue: (r: TreeRow) => string | number, colorize?: (r: TreeRow) => string | undefined) =>
    (_: any, r: TreeRow) => {
      const color = colorize?.(r);
      return (
        <span style={{ fontWeight: r.isManager ? 600 : 400, color }}>{getValue(r)}</span>
      );
    };

  const columns = useMemo(() => [
    {
      title: "团队 / 销售员",
      dataIndex: "name",
      width: 240,
      render: (v: string, r: TreeRow) =>
        r.isManager ? (
          // inline-flex：跟随 Semi 的缩进/展开图标同行排列（块级元素会换行导致缩进丢失）
          <span style={{ display: "inline-flex", alignItems: "center", gap: 8, whiteSpace: "nowrap", verticalAlign: "middle" }}>
            <span style={{ fontWeight: 600, fontSize: 13 }}>{v}</span>
            <Tag size="small" color="blue">{r.children.length}人</Tag>
            {r.sub && (
              <span style={{ fontSize: 11, color: COLORS.textTertiary, maxWidth: 120, overflow: "hidden", textOverflow: "ellipsis" }}>
                {r.sub}
              </span>
            )}
          </span>
        ) : (
          <span style={{ display: "inline-flex", alignItems: "baseline", gap: 8, whiteSpace: "nowrap", verticalAlign: "middle" }}>
            <span style={{ fontWeight: 500 }}>{v}</span>
            {r.sub && (
              <span style={{ fontSize: 11, color: COLORS.textTertiary, maxWidth: 120, overflow: "hidden", textOverflow: "ellipsis" }}>
                {r.sub}
              </span>
            )}
          </span>
        ),
    },
    { title: sortHeader("联系客户数", "contactCustomers"), dataIndex: "m", width: 110, render: numRender((r) => r.metrics.contactCustomers) },
    { title: sortHeader("通话次数", "callCount"), dataIndex: "m", width: 100, render: numRender((r) => r.metrics.callCount) },
    {
      title: sortHeader("接通次数", "connectedCount"), dataIndex: "m", width: 100,
      render: numRender((r) => r.metrics.connectedCount, (r) => (r.metrics.connectedCount > 0 ? COLORS.success : undefined)),
    },
    {
      title: sortHeader("未接通次数", "unconnectedCount"), dataIndex: "m", width: 110,
      render: numRender((r) => r.metrics.unconnectedCount, (r) => (r.metrics.unconnectedCount > 0 ? COLORS.danger : undefined)),
    },
    { title: sortHeader("通话时长", "totalDuration"), dataIndex: "m", width: 110, render: numRender((r) => formatHours(r.metrics.totalDuration)) },
    { title: sortHeader("平均通话时长", "avgDuration"), dataIndex: "m", width: 120, render: numRender((r) => formatDuration(r.metrics.avgDuration)) },
    {
      title: "接通率", dataIndex: "m", width: 140,
      render: (_: any, r: TreeRow) => {
        const v = r.metrics.connectRate;
        const color = v >= 80 ? COLORS.success : v >= 50 ? COLORS.warning : COLORS.danger;
        return (
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <Progress percent={v} size="small" style={{ width: 60 }} stroke={color} showInfo={false} />
            <span style={{ fontSize: 12, color, fontWeight: r.isManager ? 600 : 500 }}>{percentText(v)}</span>
          </div>
        );
      },
    },
    { title: sortHeader("有效通话", "effectiveCount"), dataIndex: "m", width: 100, render: numRender((r) => r.metrics.effectiveCount) },
    // eslint-disable-next-line react-hooks/exhaustive-deps
  ], [sort]);

  // ===== KPI =====
  const kpiCards = useMemo(() => {
    const s = summary;
    if (!s) return [];
    return [
      { title: "联系客户数", value: s.contactCustomers, icon: <IconUser />, color: "#3370ff", subtitle: "时间范围内去重客户" },
      { title: "通话次数", value: s.callCount, icon: <IconPhone />, color: "#722ed1", subtitle: `有效 ${s.effectiveCount} 次` },
      { title: "接通次数", value: s.connectedCount, icon: <IconTick />, color: COLORS.success, subtitle: `占比 ${s.callCount > 0 ? ((s.connectedCount * 100) / s.callCount).toFixed(1) : 0}%` },
      { title: "未接通次数", value: s.unconnectedCount, icon: <IconPhone />, color: COLORS.danger, subtitle: `占比 ${s.callCount > 0 ? ((s.unconnectedCount * 100) / s.callCount).toFixed(1) : 0}%` },
      { title: "通话时长", value: formatHours(s.totalDuration), icon: <IconClock />, color: "#13c2c2", subtitle: `平均 ${formatDuration(s.avgDuration)}/通` },
    ];
  }, [summary]);

  return (
    <div style={{ background: COLORS.bgPage, minHeight: "100vh", padding: "16px 24px" }}>
      {/* ===== 筛选区 ===== */}
      <Card style={{ marginBottom: 16 }} bodyStyle={{ padding: "12px 8px" }}>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 12, alignItems: "center" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <IconCalendar style={{ color: COLORS.textSecondary }} />
            <DatePicker
              type="dateRange"
              value={dateRange}
              onChange={(d: any) => d && setDateRange(d)}
              size="small"
              style={{ width: 260 }}
            />
          </div>

          <div style={{ display: "flex", gap: 4 }}>
            {[
              { key: "today", label: "今日" },
              { key: "7d", label: "近7天" },
              { key: "30d", label: "近30天" },
              { key: "month", label: "本月" },
            ].map((item) => (
              <Button key={item.key} size="small" theme="light" onClick={() => handlePreset(item.key as any)}>
                {item.label}
              </Button>
            ))}
          </div>

          <Divider layout="vertical" />

          <TreeSelect
            style={{ width: 200 }}
            size="small"
            value={saleId ?? undefined}
            onChange={(v: any) => setSaleId(v ?? null)}
            placeholder="选择销售（含下级）"
            treeData={managerTree}
            filterTreeNode
            showClear
            leafOnly
          />

          <div style={{ flex: 1 }} />

          <Button icon={<IconRefresh />} type="primary" onClick={handleFilter}>查询</Button>
        </div>
      </Card>

      {/* ===== KPI ===== */}
      <div style={{ display: "flex", gap: 12, marginBottom: 16, flexWrap: "wrap" }}>
        {kpiCards.map((card, i) => (
          <div key={i} style={{ flex: 1, minWidth: 200 }}>
            <KpiCard {...card} />
          </div>
        ))}
      </div>

      {/* ===== 树形明细 ===== */}
      <Card
        title="团队 & 销售员通话明细"
        bodyStyle={{ padding: 8 }}
      >
        <Spin spinning={loading}>
          <Table<TreeRow>
            columns={columns as any}
            dataSource={treeData}
            rowKey="key"
            pagination={false}
            scroll={{ x: 1100, y: 640 }}
            size="small"
            expandedRowKeys={expandedKeys}
            indentSize={28}
            onExpand={(expanded, rowData) => {
              const k = (rowData as TreeRow).key;
              setExpandedKeys((keys) => (expanded ? [...keys, k] : keys.filter((x) => x !== k)));
            }}
            onRow={(record: TreeRow) =>
              record.isManager ? { style: { background: "var(--semi-color-primary-light-default)" } } : {}
            }
            empty={<Empty description="暂无数据" />}
          />
        </Spin>
        <div style={{ padding: "4px 8px", fontSize: 12, color: COLORS.textTertiary }}>
          父节点（主管行）= 本人 + 所有下级汇总；"联系客户数"父级为成员求和，可能大于去重客户数。点击表头数字列可排序。
        </div>
      </Card>
    </div>
  );
}
